import { t } from "@travily/i18n";
import { Dialog } from "@ui/components/Dialog";
import { Icon, type IconProps } from "@ui/components/Icon";
import { SectionCard } from "@ui/components/SectionCard";
import { useState } from "react";

const NS = "components.views.programs.show";

// i18n-tasks-use t('components.views.programs.show.influences_title')
// i18n-tasks-use t('components.views.programs.show.influences_description')
// i18n-tasks-use t('components.views.programs.show.influences_show_all')

// i18n-tasks-use t('components.views.programs.show.influence_worldschooling_title')
// i18n-tasks-use t('components.views.programs.show.influence_worldschooling_description')
// i18n-tasks-use t('components.views.programs.show.influence_montessori_title')
// i18n-tasks-use t('components.views.programs.show.influence_montessori_description')
// i18n-tasks-use t('components.views.programs.show.influence_waldorf_steiner_title')
// i18n-tasks-use t('components.views.programs.show.influence_waldorf_steiner_description')
// i18n-tasks-use t('components.views.programs.show.influence_forest_school_title')
// i18n-tasks-use t('components.views.programs.show.influence_forest_school_description')
// i18n-tasks-use t('components.views.programs.show.influence_self_directed_title')
// i18n-tasks-use t('components.views.programs.show.influence_self_directed_description')
// i18n-tasks-use t('components.views.programs.show.influence_curriculum_based_title')
// i18n-tasks-use t('components.views.programs.show.influence_curriculum_based_description')
// i18n-tasks-use t('components.views.programs.show.influence_faith_based_title')
// i18n-tasks-use t('components.views.programs.show.influence_faith_based_description')
const INFLUENCE_ICONS = {
  worldschooling: "globeStand",
  montessori: "cubes",
  waldorf_steiner: "rainbow",
  forest_school: "tree",
  self_directed: "compass",
  curriculum_based: "clipboardListCheck",
  faith_based: "personPraying",
} as const satisfies Record<string, IconProps["name"]>;

const KNOWN_INFLUENCES = Object.keys(INFLUENCE_ICONS) as KnownInfluence[];

type KnownInfluence = keyof typeof INFLUENCE_ICONS;

/** The influence glyph on its tinted chip, sized to match the Figma item icon. */
function InfluenceIcon({ influence, size }: { influence: KnownInfluence; size: "grid" | "modal" }) {
  const chip = size === "grid" ? "size-8 rounded-lg xl:size-10" : "size-10 shrink-0 rounded-lg";
  const glyph = "size-5";
  return (
    <span aria-hidden className={`flex items-center justify-center bg-green-100 text-green-700 ${chip}`}>
      <Icon className={glyph} name={INFLUENCE_ICONS[influence]} />
    </span>
  );
}

/** How many influences render in the card before overflowing into the modal. */
const ABOVE_FOLD_COUNT = 6;

export interface InfluencesCardProps {
  /** `program.influences` slugs; unknown slugs are ignored. */
  influences: string[];
  className?: string;
}

/**
 * "Sparks & Influences" program section: the educational approaches behind the
 * program as an icon grid, overflowing into a show-all modal past
 * {@link ABOVE_FOLD_COUNT}. Renders nothing when the program lists no (known)
 * influences.
 */
export function InfluencesCard({ influences, className }: InfluencesCardProps) {
  const [showAllOpen, setShowAllOpen] = useState(false);
  const known = influences.filter(isKnownInfluence);
  if (known.length === 0) return null;

  const aboveFold = known.slice(0, ABOVE_FOLD_COUNT);

  return (
    <SectionCard className={className} theme="green">
      <SectionCard.Content>
        <SectionCard.Header
          description={t(`${NS}.influences_description`)}
          icon={<Icon className="size-8 xl:size-10" color="green" name="lightbulb" type="sticker" />}
          title={t(`${NS}.influences_title`)}
        />
        <SectionCard.ItemGrid>
          {aboveFold.map((influence) => (
            <SectionCard.Item
              description={t(`${NS}.influence_${influence}_description`)}
              icon={<InfluenceIcon influence={influence} size="grid" />}
              key={influence}
              title={t(`${NS}.influence_${influence}_title`)}
            />
          ))}
        </SectionCard.ItemGrid>
        {known.length > ABOVE_FOLD_COUNT && (
          <>
            <SectionCard.ShowAll onPress={() => setShowAllOpen(true)}>
              {t(`${NS}.influences_show_all`, { count: known.length })}
            </SectionCard.ShowAll>
            <Dialog.Root onOpenChange={setShowAllOpen} isOpen={showAllOpen} size="lg">
              <Dialog.Content>
                <Dialog.Header title={t(`${NS}.tab_influences`)} />
                <Dialog.Body>
                  <ul>
                    {known.map((influence) => (
                      <li
                        className="flex gap-4 border-neutral-100 border-b py-3 first:pt-0 last:border-b-0"
                        key={influence}
                      >
                        <InfluenceIcon influence={influence} size="modal" />
                        <div className="flex flex-col gap-1">
                          <h3 className="font-semibold text-lg text-neutral-900 leading-6">
                            {t(`${NS}.influence_${influence}_title`)}
                          </h3>
                          <p className="text-base text-neutral-600 leading-6">
                            {t(`${NS}.influence_${influence}_description`)}
                          </p>
                        </div>
                      </li>
                    ))}
                  </ul>
                </Dialog.Body>
              </Dialog.Content>
            </Dialog.Root>
          </>
        )}
      </SectionCard.Content>
    </SectionCard>
  );
}

function isKnownInfluence(value: string): value is KnownInfluence {
  return (KNOWN_INFLUENCES as readonly string[]).includes(value);
}

/**
 * Whether any of the given slugs render in this card — mirrors the card's own
 * empty check, so section registries can gate on the same condition instead of
 * the raw array length.
 */
export function hasKnownInfluences(influences: string[]): boolean {
  return influences.some(isKnownInfluence);
}
